import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload, Loader2, CheckCircle, AlertCircle, FileText,
  Trash2, RefreshCw, Braces, X, Save, Sparkles, Building2, Car,
  ChevronDown, ChevronRight, Search,
} from 'lucide-react';
import { enrichmentService, EnrichmentSource, VehicleTags, DocumentDetails } from '../../../services/EnrichmentService';
import { adminService } from '../../../services/AdminService';

const ACCEPTED = '.pdf,.xlsx,.xls,.csv,.txt,.json';

// Over this, the multipart endpoint can't carry the file (Cloud Run 32MiB
// body cap, 20MB extraction limit) — the browser PUTs straight to GCS via a
// signed URL instead and the document is served as an attached reference.
const SIGNED_UPLOAD_THRESHOLD = 20 * 1024 * 1024;

interface EnrichmentSourcesSectionProps {
  /** 'org' — the organisation's own sources (Settings → Data Sources);
   *  'admin' — platform-shared sources with org assignment (Admin → Shared Integrations). */
  mode: 'org' | 'admin';
  /** Full org list for assignment chips (admin mode only). */
  organizations?: any[];
}

/**
 * Enrichment sources: client documents (maintenance guides, model-name
 * sheets, …) converted to JSON; the "Ground Order Enrichment" workflow step
 * attaches the vehicle-relevant data to each order. Embedded inside the Data
 * Sources page (org mode) and the admin Shared Integrations tab (admin mode)
 * so enrichment lives with the rest of the data-source management.
 */
const EnrichmentSourcesSection: React.FC<EnrichmentSourcesSectionProps> = ({ mode, organizations = [] }) => {
  const { t } = useTranslation();
  const isAdmin = mode === 'admin';

  // Master switch: in org mode the whole section is hidden unless the GLOBAL
  // external_enrichment flag is on (admin mode always shows — the platform
  // admin manages/preps sources regardless of the flag).
  const [flagEnabled, setFlagEnabled] = useState<boolean | null>(isAdmin ? true : null);
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    adminService.getGlobalFeatureFlagPublic('external_enrichment')
      .then(res => { if (!cancelled) setFlagEnabled(res.data?.is_enabled ?? false); })
      .catch(() => { if (!cancelled) setFlagEnabled(false); });
    return () => { cancelled = true; };
  }, [isAdmin]);
  const [sources, setSources] = useState<EnrichmentSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Upload form — multi-select: one shared context/org assignment per batch,
  // per-file names derived from filenames when more than one is picked.
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [uploadContext, setUploadContext] = useState('');
  const [uploadOrgIds, setUploadOrgIds] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  // Direct-to-GCS progress % (null while not in the signed-URL flow)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // 1-based position within a multi-file batch (null for single uploads)
  const [batchIndex, setBatchIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // JSON editor modal
  const [editorSource, setEditorSource] = useState<EnrichmentSource | null>(null);
  const [editorText, setEditorText] = useState('');
  const [editorVersion, setEditorVersion] = useState<number | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  // Document-details editor (attached documents): coverage + title + type
  const [tagsSource, setTagsSource] = useState<EnrichmentSource | null>(null);
  const [tagsForm, setTagsForm] = useState<{ make: string; models: string; yearFrom: string; yearTo: string; title: string; docType: string }>({ make: '', models: '', yearFrom: '', yearTo: '', title: '', docType: 'other' });
  const [tagsSaving, setTagsSaving] = useState(false);

  // Org-assignment modal (admin only). orgsBulk=true → the selection applies
  // to every selected SHARED document instead of a single source.
  const [orgsSource, setOrgsSource] = useState<EnrichmentSource | null>(null);
  const [orgsBulk, setOrgsBulk] = useState(false);
  const [orgsSelection, setOrgsSelection] = useState<number[]>([]);
  const [orgsSaving, setOrgsSaving] = useState(false);

  // Extraction-prompt editor (admin only; loaded lazily on expand)
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptBody, setPromptBody] = useState('');
  const [promptDefaultBody, setPromptDefaultBody] = useState('');
  const [promptContract, setPromptContract] = useState('');
  const [promptIsDefault, setPromptIsDefault] = useState(true);
  const [promptLoaded, setPromptLoaded] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);

  const loadPrompt = async () => {
    try {
      const res = await enrichmentService.adminGetExtractionPrompt();
      setPromptBody(res.data.body);
      setPromptDefaultBody(res.data.default_body);
      setPromptContract(res.data.contract);
      setPromptIsDefault(res.data.is_default);
      setPromptLoaded(true);
    } catch {
      showBanner('error', t('enrichment.promptLoadFailed', 'The extraction prompt could not be loaded.'));
    }
  };

  const savePrompt = async (body: string) => {
    setPromptSaving(true);
    try {
      const res = await enrichmentService.adminSetExtractionPrompt(body);
      setPromptBody(res.data.body);
      setPromptIsDefault(res.data.is_default);
      showBanner('success', t('enrichment.promptSaved', 'Extraction prompt saved — applies to future extractions; use Re-extract on a source to apply it there.'));
    } catch (e: any) {
      showBanner('error', e?.response?.data?.detail || t('enrichment.promptSaveFailed', 'The extraction prompt could not be saved.'));
    } finally {
      setPromptSaving(false);
    }
  };

  const orgName = (id: number) =>
    organizations.find(o => o.id === id)?.name || `Org ${id}`;

  // Organisations grouped by location (settings.country) — assignment across
  // many client orgs is location-driven, so the pickers show one block per
  // location, unlocated orgs last.
  const orgsByLocation: Array<{ location: string; orgs: any[] }> = React.useMemo(() => {
    const groups = new Map<string, any[]>();
    for (const org of organizations) {
      const loc = (org.location || '').trim().toUpperCase();
      const key = loc || '__none__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(org);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => (a === '__none__' ? 1 : b === '__none__' ? -1 : a.localeCompare(b)))
      .map(([key, orgs]) => ({
        location: key === '__none__' ? '' : key,
        orgs: orgs.sort((a, b) => String(a.name).localeCompare(String(b.name))),
      }));
  }, [organizations]);

  const renderOrgChips = (selection: number[], toggle: (id: number) => void) => (
    <div className="space-y-2.5">
      {orgsByLocation.map(group => (
        <div key={group.location || 'none'}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">
            {group.location || t('enrichment.noLocation', 'No location set')}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.orgs.map(org => (
              <button
                key={org.id}
                onClick={() => toggle(org.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selection.includes(org.id)
                  ? 'bg-teal-50 text-teal-700 border-teal-300'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}
              >
                {org.name}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const showBanner = (kind: 'success' | 'error', text: string) => {
    setBanner({ kind, text });
    setTimeout(() => setBanner(null), 6000);
  };

  const listSources = useCallback(
    () => (isAdmin ? enrichmentService.adminListSources() : enrichmentService.listSources()),
    [isAdmin],
  );

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listSources();
      setSources(res.data?.sources || []);
    } catch {
      if (!silent) showBanner('error', t('enrichment.loadFailed', 'Enrichment sources could not be loaded.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [listSources, t]);

  useEffect(() => {
    if (flagEnabled === true) refresh();
  }, [refresh, flagEnabled]);

  // Poll while any source is still extracting
  const anySyncing = sources.some(s => s.status === 'syncing');
  useEffect(() => {
    if (!anySyncing) return;
    const interval = setInterval(() => refresh(true), 5000);
    return () => clearInterval(interval);
  }, [anySyncing, refresh]);

  const stripExtension = (fileName: string) => fileName.replace(/\.[^.]+$/, '');

  const handleFilesPicked = (picked: FileList | null) => {
    const list = Array.from(picked || []);
    setFiles(list);
    if (list.length === 1 && !name) setName(stripExtension(list[0].name));
    if (list.length > 1) setName('');
  };

  const toggleOrg = (list: number[], setList: (v: number[]) => void, id: number) =>
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);

  const uploadOne = async (f: File, sourceName: string): Promise<boolean> => {
    const isLarge = f.size > SIGNED_UPLOAD_THRESHOLD;
    if (isLarge) {
      // Signed-URL flow: browser → GCS directly, with real progress.
      setUploadProgress(0);
      try {
        if (isAdmin) {
          await enrichmentService.adminUploadSourceViaSignedUrl(f, sourceName, uploadOrgIds, uploadContext, setUploadProgress);
        } else {
          await enrichmentService.uploadSourceViaSignedUrl(f, sourceName, uploadContext, setUploadProgress);
        }
      } finally {
        setUploadProgress(null);
      }
    } else if (isAdmin) {
      await enrichmentService.adminUploadSource(f, sourceName, uploadOrgIds, uploadContext);
    } else {
      await enrichmentService.uploadSource(f, sourceName, uploadContext);
    }
    return isLarge;
  };

  const handleUpload = async () => {
    if (!files.length) return;
    if (files.length === 1 && !name.trim()) return;
    setUploading(true);
    const failed: string[] = [];
    let anyStaged = false;
    // Sequential on purpose: parallel PUTs just contend for upstream
    // bandwidth, and each registered source already extracts/indexes in a
    // backend background task, so the pipeline overlaps anyway.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setBatchIndex(files.length > 1 ? i + 1 : null);
      const sourceName = files.length === 1 ? name.trim() : stripExtension(f.name);
      try {
        anyStaged = (await uploadOne(f, sourceName)) || anyStaged;
        if (files.length > 1) refresh(true); // list fills in as the batch runs
      } catch {
        failed.push(f.name);
      }
    }
    setBatchIndex(null);
    setUploading(false);
    const total = files.length;
    setFiles([]);
    setName('');
    setUploadContext('');
    setUploadOrgIds([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (failed.length) {
      showBanner('error', t('enrichment.batchPartial',
        '{{ok}} of {{total}} documents uploaded — failed: {{names}}',
        { ok: total - failed.length, total, names: failed.join(', ') }));
    } else if (total > 1) {
      showBanner('success', t('enrichment.batchUploaded',
        '{{count}} documents uploaded — extraction and indexing run in the background.',
        { count: total }));
    } else {
      showBanner('success', anyStaged
        ? t('enrichment.staged', 'Uploaded — the document will be attached to answers for its vehicles.')
        : t('enrichment.uploaded', 'Extraction started — large PDFs can take a few minutes.'));
    }
    refresh(true);
  };

  const openEditor = async (source: EnrichmentSource) => {
    setEditorSource(source);
    setEditorLoading(true);
    setEditorError(null);
    try {
      const res = isAdmin
        ? await enrichmentService.adminGetJson(source.id)
        : await enrichmentService.getJson(source.id);
      setEditorText(JSON.stringify(res.data.data, null, 2));
      setEditorVersion(res.data.version);
    } catch (e: any) {
      setEditorText('');
      setEditorError(e?.response?.data?.detail || t('enrichment.jsonLoadFailed', 'The extracted JSON could not be loaded.'));
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEditor = async () => {
    if (!editorSource) return;
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(editorText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
    } catch {
      setEditorError(t('enrichment.invalidJson', 'This is not a valid JSON object — fix the syntax before saving.'));
      return;
    }
    setEditorSaving(true);
    setEditorError(null);
    try {
      if (isAdmin) await enrichmentService.adminSaveJson(editorSource.id, parsed);
      else await enrichmentService.saveJson(editorSource.id, parsed);
      setEditorSource(null);
      showBanner('success', t('enrichment.saved', 'A new version was published — future orders will use the updated data.'));
      refresh(true);
    } catch (e: any) {
      setEditorError(e?.response?.data?.detail || t('enrichment.saveFailed', 'The JSON could not be saved.'));
    } finally {
      setEditorSaving(false);
    }
  };

  const openOrgs = (source: EnrichmentSource) => {
    setOrgsBulk(false);
    setOrgsSource(source);
    setOrgsSelection(source.shared_organization_ids || []);
  };

  const bulkShared = () => selectedSources.filter(s => s.organization_id == null);

  const openBulkOrgs = () => {
    setOrgsSource(null);
    setOrgsBulk(true);
    setOrgsSelection([]);
  };

  const closeOrgs = () => { setOrgsSource(null); setOrgsBulk(false); };

  const saveOrgs = async () => {
    setOrgsSaving(true);
    try {
      if (orgsBulk) {
        const targets = bulkShared();
        const failed: string[] = [];
        for (const s of targets) {
          try { await enrichmentService.adminSetOrganizations(s.id, orgsSelection); }
          catch { failed.push(titleOf(s)); }
        }
        clearSelection();
        if (failed.length) {
          showBanner('error', t('enrichment.bulkPartial', '{{ok}} of {{total}} done — failed: {{names}}',
            { ok: targets.length - failed.length, total: targets.length, names: failed.join(', ') }));
        } else {
          showBanner('success', t('enrichment.bulkOrgsSaved', 'Assignments updated on {{count}} documents.', { count: targets.length }));
        }
      } else if (orgsSource) {
        await enrichmentService.adminSetOrganizations(orgsSource.id, orgsSelection);
      }
      closeOrgs();
      refresh(true);
    } catch (e: any) {
      showBanner('error', e?.response?.data?.detail || t('enrichment.orgsSaveFailed', 'Organisation assignments could not be saved.'));
    } finally {
      setOrgsSaving(false);
    }
  };

  const openTags = (source: EnrichmentSource) => {
    const tags = source.vehicleTags || {};
    setTagsForm({
      make: tags.make || '',
      models: (tags.models || []).join(', '),
      yearFrom: tags.year_from != null ? String(tags.year_from) : '',
      yearTo: tags.year_to != null ? String(tags.year_to) : '',
      title: source.displayTitle || '',
      docType: source.docType || 'other',
    });
    setTagsSource(source);
  };

  const saveTags = async () => {
    if (!tagsSource) return;
    setTagsSaving(true);
    try {
      const payload: DocumentDetails = {
        make: tagsForm.make.trim() || null,
        models: tagsForm.models.split(',').map(m => m.trim()).filter(Boolean),
        year_from: tagsForm.yearFrom.trim() ? parseInt(tagsForm.yearFrom, 10) : null,
        year_to: tagsForm.yearTo.trim() ? parseInt(tagsForm.yearTo, 10) : null,
        title: tagsForm.title.trim() || null,
        doc_type: tagsForm.docType,
      };
      await enrichmentService.updateVehicleTags(tagsSource.id, payload, isAdmin);
      setTagsSource(null);
      showBanner('success', t('enrichment.tagsSaved', 'Document details updated — answers use the new coverage immediately.'));
      refresh(true);
    } catch (e: any) {
      showBanner('error', e?.response?.data?.detail || t('enrichment.tagsSaveFailed', 'The vehicle tags could not be saved.'));
    } finally {
      setTagsSaving(false);
    }
  };



  // ── Library organisation: search / filters / grouping / selection ─────────
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [view, setView] = useState<'list' | 'coverage'>('list');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);

  const titleOf = (s: EnrichmentSource) => s.displayTitle || s.name;
  const canManage = (s: EnrichmentSource) => isAdmin || s.organization_id != null;
  /** Facet used for filtering/sorting: injected docs are their own bucket,
   *  attach docs facet by docType. */
  const effType = (s: EnrichmentSource) =>
    s.mode === 'attach' ? (s.docType || 'other') : 'injected';
  const TYPE_ORDER = ['owner_manual', 'maintenance', 'warranty', 'safety', 'spec_sheet', 'other', 'injected'];
  const typeLabel = (tKey: string) => t(`enrichment.docType.${tKey}`, tKey.replace('_', ' '));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sources.filter(s => {
      if (typeFilter !== 'all' && effType(s) !== typeFilter) return false;
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        titleOf(s), s.name, s.fileName, s.vehicleTags?.make,
        ...(s.vehicleTags?.models || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources, query, typeFilter, statusFilter]);

  /** Vehicle groups: "MAKE · model / model" from tags; everything without a
   *  make (all injected docs, untagged attach docs) lands in one bucket at
   *  the end. */
  const groups = useMemo(() => {
    const map = new Map<string, EnrichmentSource[]>();
    for (const s of filtered) {
      const make = (s.vehicleTags?.make || '').trim();
      const models = (s.vehicleTags?.models || []).join(' / ');
      const key = make ? `${make}${models ? ' · ' + models : ''}` : '__untagged__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    const entries = Array.from(map.entries());
    entries.sort(([a], [b]) =>
      a === '__untagged__' ? 1 : b === '__untagged__' ? -1 : a.localeCompare(b));
    for (const [, list] of entries) {
      list.sort((x, y) =>
        TYPE_ORDER.indexOf(effType(x)) - TYPE_ORDER.indexOf(effType(y))
        || titleOf(x).localeCompare(titleOf(y)));
    }
    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  /** Coverage matrix rows: one line per tagged (make, model), counting
   *  attach documents per type — the "what's missing" view. */
  const coverage = useMemo(() => {
    const rows = new Map<string, Map<string, number>>();
    for (const s of sources) {
      if (s.mode !== 'attach') continue;
      const make = (s.vehicleTags?.make || '').trim();
      if (!make) continue;
      const models = s.vehicleTags?.models?.length ? s.vehicleTags.models : ['—'];
      for (const m of models) {
        const key = `${make} ${m}`;
        if (!rows.has(key)) rows.set(key, new Map());
        const r = rows.get(key)!;
        const tKey = s.docType || 'other';
        r.set(tKey, (r.get(tKey) || 0) + 1);
      }
    }
    return Array.from(rows.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [sources]);

  const toggleCollapsed = (key: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const toggleSelect = (id: number) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroupSelect = (list: EnrichmentSource[]) => {
    const ids = list.filter(canManage).map(s => s.id);
    setSelected(prev => {
      const next = new Set(prev);
      const allIn = ids.every(id => next.has(id));
      ids.forEach(id => allIn ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const selectedSources = sources.filter(s => selected.has(s.id) && canManage(s));
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (
    kind: string,
    action: (s: EnrichmentSource) => Promise<any>,
    doneText: string,
  ) => {
    setBulkBusy(kind);
    const failed: string[] = [];
    for (const s of selectedSources) {
      try { await action(s); } catch { failed.push(titleOf(s)); }
    }
    setBulkBusy(null);
    clearSelection();
    refresh(true);
    if (failed.length) {
      showBanner('error', t('enrichment.bulkPartial', '{{ok}} of {{total}} done — failed: {{names}}',
        { ok: selectedSources.length - failed.length, total: selectedSources.length, names: failed.join(', ') }));
    } else {
      showBanner('success', doneText);
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(t('enrichment.bulkDeleteConfirm',
      'Delete {{count}} documents? New orders and answers will no longer use them.',
      { count: selectedSources.length }))) return;
    await runBulk('delete',
      (s) => isAdmin ? enrichmentService.adminDeleteSource(s.id) : enrichmentService.deleteSource(s.id),
      t('enrichment.bulkDeleted', '{{count}} documents deleted.', { count: selectedSources.length }));
  };

  const bulkReprocess = async () => {
    await runBulk('reprocess',
      (s) => isAdmin ? enrichmentService.adminReExtract(s.id) : enrichmentService.reExtract(s.id),
      t('enrichment.bulkReprocessed', 'Re-processing started for {{count}} documents.', { count: selectedSources.length }));
  };

  const handleReExtract = async (source: EnrichmentSource) => {
    if (source.status === 'syncing' && !window.confirm(
      t('enrichment.restartConfirm', '"{{name}}" is still extracting. Restart the extraction from the beginning?', { name: source.name }),
    )) return;
    try {
      if (isAdmin) await enrichmentService.adminReExtract(source.id);
      else await enrichmentService.reExtract(source.id);
      showBanner('success', t('enrichment.reExtract', 'Re-extraction started — current data stays live until it finishes.'));
      refresh(true);
    } catch (e: any) {
      showBanner('error', e?.response?.data?.detail || t('enrichment.reExtractFailed', 'Extraction could not be restarted.'));
    }
  };

  const handleDelete = async (source: EnrichmentSource) => {
    const assigned = (source.shared_organization_ids || []).length;
    const warning = isAdmin && assigned
      ? t('enrichment.deleteConfirmShared', 'Delete "{{name}}"?\n\n{{count}} organisation(s) will stop receiving this data on new orders.', { name: source.name, count: assigned })
      : t('enrichment.deleteConfirm', 'Delete "{{name}}"? New orders will no longer be enriched from it.', { name: source.name });
    if (!window.confirm(warning)) return;
    try {
      if (isAdmin) await enrichmentService.adminDeleteSource(source.id);
      else await enrichmentService.deleteSource(source.id);
      refresh(true);
    } catch {
      showBanner('error', t('enrichment.deleteFailed', 'The source could not be deleted.'));
    }
  };


  const statusBadge = (source: EnrichmentSource) => {
    switch (source.status) {
      case 'syncing':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <Loader2 className="w-3 h-3 animate-spin" />
            {source.mode === 'attach'
              ? t('enrichment.status.analyzing', 'Analyzing')
              : t('enrichment.status.syncing', 'Extracting')}
            {source.progress ? ` ${source.progress}` : '…'}
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            <CheckCircle className="w-3 h-3" />
            {t('enrichment.status.active', 'Active')} · v{source.version}
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200" title={source.error || ''}>
            <AlertCircle className="w-3 h-3" />
            {t('enrichment.status.error', 'Failed')}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
            {source.status}
          </span>
        );
    }
  };

  // Still resolving the flag in org mode → render nothing yet.
  if (flagEnabled === null) return null;
  // Flag off in org mode: the section now lives in its own sub-tab, so a
  // blank panel would look broken — say why it's empty instead.
  if (flagEnabled === false) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
        <Sparkles className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">
          {t('enrichment.flagOff', 'Reference documents are not enabled for your organisation — contact support to activate them.')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* The section title lives on the parent's sub-tab — only the
          explanatory copy renders here. */}
      <div className="mb-5">
        <p className="text-sm text-gray-500 max-w-3xl">
          {isAdmin
            ? t('enrichment.descriptionAdmin', 'Client documents (maintenance guides, model-name sheets, warranty terms) shared across organisations. Small documents are converted to JSON and injected into each matching order; large documents (manuals) are attached whole to answers for their matching vehicles.')
            : t('enrichment.description', 'Upload client documents (maintenance guides, model-name sheets, warranty terms). Small documents are converted to JSON you can review and are injected into each matching order; large documents (manuals) are attached whole to answers for their matching vehicles.')}
        </p>
      </div>

      {banner && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm border ${banner.kind === 'success'
          ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-red-50 text-red-700 border-red-200'}`}
        >
          {banner.text}
        </div>
      )}

      {/* Upload card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-4">{t('enrichment.uploadTitle', 'Add a document')}</h3>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            multiple
            onChange={e => handleFilesPicked(e.target.files)}
            className="text-sm text-gray-600 file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 file:text-xs file:font-medium hover:file:bg-gray-200 file:cursor-pointer"
          />
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={files.length > 1}
            placeholder={files.length > 1
              ? t('enrichment.namesFromFiles', '{{count}} files — names taken from the filenames', { count: files.length })
              : t('enrichment.namePlaceholder', 'Source name (e.g. Pauta Mantención MG 2025)')}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 disabled:bg-gray-50 disabled:text-gray-400"
          />
          <button
            onClick={handleUpload}
            disabled={!files.length || (files.length === 1 && !name.trim()) || uploading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading && batchIndex !== null
              ? t('enrichment.uploadingBatch', 'Uploading {{i}}/{{n}}…', { i: batchIndex, n: files.length })
                + (uploadProgress !== null ? ` ${uploadProgress}%` : '')
              : uploadProgress !== null
                ? t('enrichment.uploadingPct', 'Uploading… {{pct}}%', { pct: uploadProgress })
                : files.length > 1
                  ? t('enrichment.uploadMany', 'Upload {{count}} documents', { count: files.length })
                  : t('enrichment.upload', 'Upload & extract')}
          </button>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            {t('enrichment.contextLabel', 'Parsing context')}{' '}
            <span className="text-gray-400 font-normal">{t('enrichment.optional', '(optional)')}</span>
          </label>
          <textarea
            value={uploadContext}
            onChange={e => setUploadContext(e.target.value)}
            maxLength={2000}
            rows={3}
            placeholder={t('enrichment.contextPlaceholder', 'Tell the parser what this document is and how to read it — terminology, column meanings, currency and units.')}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-y"
          />
          <p className="text-[11px] text-gray-400 mt-1">
            {t('enrichment.contextHint', 'Sent with the file to the extraction step (and every re-extract).')}
          </p>
        </div>
        {isAdmin && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">
              {t('enrichment.shareWith', 'Share with organisations (editable later):')}
            </p>
            {renderOrgChips(uploadOrgIds, (id) => toggleOrg(uploadOrgIds, setUploadOrgIds, id))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">
          {t('enrichment.uploadHint', 'PDF, Excel, CSV or text. Documents up to 20MB are extracted for injection; larger PDFs upload directly to storage and are attached to answers for their vehicles.')}
        </p>
      </div>

      {/* Extraction prompt editor — platform admin only */}
      {isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 mb-6 overflow-hidden">
          <button
            type="button"
            onClick={() => { setPromptOpen(v => !v); if (!promptLoaded) loadPrompt(); }}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
              <Braces className="w-4 h-4 text-gray-400" />
              {t('enrichment.promptTitle', 'Extraction prompt')}
              {!promptIsDefault && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  {t('enrichment.promptCustom', 'customised')}
                </span>
              )}
            </span>
            <span className="text-xs text-gray-400">{promptOpen ? '▲' : '▼'}</span>
          </button>
          {promptOpen && (
            <div className="px-5 pb-5 space-y-3">
              <p className="text-xs text-gray-500">
                {t('enrichment.promptHint', 'The instruction Gemini receives with every document (all chunks). Applies to FUTURE extractions — use Re-extract on a source to apply changes to it.')}
              </p>
              <textarea
                value={promptBody}
                onChange={e => setPromptBody(e.target.value)}
                maxLength={4000}
                rows={5}
                spellCheck={false}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-y"
              />
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  {t('enrichment.promptContractLabel', 'Always appended (not editable) — keeps the output machine-readable')}
                </p>
                <p className="text-[11px] font-mono text-gray-500 whitespace-pre-wrap">{promptContract}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => savePrompt(promptBody)}
                  disabled={promptSaving || !promptBody.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 transition-colors"
                >
                  {promptSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {t('enrichment.promptSave', 'Save prompt')}
                </button>
                {!promptIsDefault && (
                  <button
                    onClick={() => savePrompt('')}
                    disabled={promptSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t('enrichment.promptReset', 'Reset to default')}
                  </button>
                )}
                {promptIsDefault && promptBody !== promptDefaultBody && promptDefaultBody && (
                  <button
                    onClick={() => setPromptBody(promptDefaultBody)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t('enrichment.promptUndo', 'Undo edits')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Library: toolbar + grouped list / coverage matrix */}
      {loading ? (
        <div className="flex items-center justify-center py-12 gap-3">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="text-sm text-gray-500">{t('enrichment.loading', 'Loading sources…')}</span>
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {t('enrichment.empty', 'No reference documents yet — upload the first client document above.')}
          </p>
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('enrichment.searchPlaceholder', 'Search title, file, model…')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
              />
            </div>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="px-2.5 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-600"
            >
              <option value="all">{t('enrichment.filterAllTypes', 'All types')}</option>
              <option value="injected">{t('enrichment.docType.injected', 'Injected data')}</option>
              <option value="owner_manual">{t('enrichment.docType.owner_manual', 'Owner manual')}</option>
              <option value="maintenance">{t('enrichment.docType.maintenance', 'Maintenance')}</option>
              <option value="warranty">{t('enrichment.docType.warranty', 'Warranty')}</option>
              <option value="safety">{t('enrichment.docType.safety', 'Safety')}</option>
              <option value="spec_sheet">{t('enrichment.docType.spec_sheet', 'Spec sheet')}</option>
              <option value="other">{t('enrichment.docType.other', 'Other')}</option>
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-2.5 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-600"
            >
              <option value="all">{t('enrichment.filterAllStatuses', 'Any status')}</option>
              <option value="active">{t('enrichment.status.active', 'Active')}</option>
              <option value="syncing">{t('enrichment.filterProcessing', 'Processing')}</option>
              <option value="error">{t('enrichment.status.error', 'Failed')}</option>
            </select>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => setView('list')}
                className={`px-3 py-2 text-xs font-medium ${view === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >
                {t('enrichment.viewList', 'Documents')}
              </button>
              <button
                onClick={() => setView('coverage')}
                className={`px-3 py-2 text-xs font-medium ${view === 'coverage' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              >
                {t('enrichment.viewCoverage', 'Coverage')}
              </button>
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {t('enrichment.docCount', '{{shown}} of {{total}} documents', { shown: filtered.length, total: sources.length })}
            </span>
          </div>

          {/* Bulk-action bar */}
          {selectedSources.length > 0 && (
            <div className="sticky top-0 z-10 flex items-center gap-2 mb-3 px-4 py-2.5 bg-gray-900 text-white rounded-lg shadow">
              <span className="text-xs font-medium">
                {t('enrichment.selectedCount', '{{count}} selected', { count: selectedSources.length })}
              </span>
              <div className="flex-1" />
              {isAdmin && bulkShared().length > 0 && (
                <button
                  onClick={openBulkOrgs}
                  disabled={bulkBusy !== null}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-40"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  {t('enrichment.bulkOrgs', 'Organisations ({{count}})', { count: bulkShared().length })}
                </button>
              )}
              <button
                onClick={bulkReprocess}
                disabled={bulkBusy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-40"
              >
                {bulkBusy === 'reprocess' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {t('enrichment.bulkReprocess', 'Re-process')}
              </button>
              <button
                onClick={bulkDelete}
                disabled={bulkBusy !== null}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-500/80 rounded-lg hover:bg-red-500 disabled:opacity-40"
              >
                {bulkBusy === 'delete' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {t('enrichment.bulkDelete', 'Delete')}
              </button>
              <button
                onClick={clearSelection}
                disabled={bulkBusy !== null}
                className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {view === 'coverage' ? (
            /* Coverage matrix: one row per tagged (make, model); counts of
               attached documents per type — the "what's missing" view. */
            coverage.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-300 text-sm text-gray-500">
                {t('enrichment.coverageEmpty', 'No vehicle-tagged documents yet — coverage appears once attached documents carry make/model tags.')}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">
                        {t('enrichment.coverageVehicle', 'Vehicle')}
                      </th>
                      {['owner_manual', 'maintenance', 'warranty', 'safety', 'spec_sheet', 'other'].map(tKey => (
                        <th key={tKey} className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                          {typeLabel(tKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {coverage.map(([vehicle, byType]) => (
                      <tr key={vehicle} className="border-b border-gray-100 last:border-0">
                        <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap">{vehicle}</td>
                        {['owner_manual', 'maintenance', 'warranty', 'safety', 'spec_sheet', 'other'].map(tKey => {
                          const n = byType.get(tKey) || 0;
                          return (
                            <td key={tKey} className="text-center px-3 py-2">
                              {n > 0 ? (
                                <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                                  <CheckCircle className="w-3.5 h-3.5" />{n > 1 ? n : ''}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-300 text-sm text-gray-500">
              {t('enrichment.noMatches', 'No documents match the current search/filters.')}
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map(([groupKey, list]) => {
                const isCollapsed = collapsed.has(groupKey);
                const manageable = list.filter(canManage);
                const allSelected = manageable.length > 0 && manageable.every(s => selected.has(s.id));
                return (
                  <div key={groupKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50/60 border-b border-gray-100">
                      {manageable.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => toggleGroupSelect(list)}
                          className="w-3.5 h-3.5 accent-gray-900"
                        />
                      )}
                      <button
                        onClick={() => toggleCollapsed(groupKey)}
                        className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
                      >
                        {isCollapsed
                          ? <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        {groupKey === '__untagged__'
                          ? <span className="text-sm font-semibold text-gray-500">{t('enrichment.groupUntagged', 'Other documents (no vehicle tags)')}</span>
                          : (
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 truncate">
                              <Car className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              {groupKey}
                            </span>
                          )}
                        <span className="text-xs text-gray-400 flex-shrink-0">({list.length})</span>
                      </button>
                    </div>
                    {!isCollapsed && list.map(source => (
                      <div key={source.id} className="flex items-center gap-2.5 px-3 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        {canManage(source) ? (
                          <input
                            type="checkbox"
                            checked={selected.has(source.id)}
                            onChange={() => toggleSelect(source.id)}
                            className="w-3.5 h-3.5 accent-gray-900 flex-shrink-0"
                          />
                        ) : <span className="w-3.5 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-gray-900 truncate" title={source.fileName || undefined}>
                              {titleOf(source)}
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200 uppercase tracking-wide">
                              {typeLabel(effType(source))}
                            </span>
                            {statusBadge(source)}
                            {isAdmin && source.organization_id != null && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500 border border-gray-200">
                                {orgName(source.organization_id)}
                              </span>
                            )}
                            {!isAdmin && source.organization_id == null && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] bg-teal-50 text-teal-700 border border-teal-200">
                                {t('enrichment.sharedShort', 'shared')}
                              </span>
                            )}
                            {isAdmin && source.organization_id == null && (source.shared_organization_ids || []).length === 0 && (
                              <span className="text-[10px] text-amber-600">
                                {t('enrichment.notAssignedShort', 'not assigned')}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate">
                            {source.fileName}
                            {source.mode === 'attach' && source.pageCount ? ` · ${t('enrichment.pageCount', '{{count}} pages', { count: source.pageCount })}` : ''}
                            {isAdmin && source.organization_id == null && (source.shared_organization_ids || []).length > 0
                              ? ` · ${(source.shared_organization_ids || []).map(orgName).join(', ')}` : ''}
                            {source.status === 'error' && source.error ? <span className="text-red-500"> — {source.error}</span> : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {isAdmin && source.organization_id == null && (
                            <button
                              onClick={() => openOrgs(source)}
                              title={t('enrichment.organisations', 'Organisations')}
                              className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                            >
                              <Building2 className="w-4 h-4" />
                            </button>
                          )}
                          {source.mode === 'attach' && canManage(source) && (
                            <button
                              onClick={() => openTags(source)}
                              title={t('enrichment.details', 'Details')}
                              className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                            >
                              <Car className="w-4 h-4" />
                            </button>
                          )}
                          {source.mode !== 'attach' && (
                            <button
                              onClick={() => openEditor(source)}
                              disabled={source.status === 'syncing'}
                              title={t('enrichment.reviewJson', 'Review JSON')}
                              className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                            >
                              <Braces className="w-4 h-4" />
                            </button>
                          )}
                          {canManage(source) && (
                            <>
                              <button
                                onClick={() => handleReExtract(source)}
                                title={source.status === 'syncing'
                                  ? t('enrichment.restartAction', 'Restart a stuck extraction')
                                  : t('enrichment.reExtractAction', 'Re-process from the stored document')}
                                className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(source)}
                                title={t('enrichment.deleteAction', 'Delete source')}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* JSON editor modal */}
      {editorSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{editorSource.name}</h3>
                <p className="text-xs text-gray-400">
                  {editorVersion !== null
                    ? t('enrichment.editorVersion', 'Version {{v}} — saving publishes a new version', { v: editorVersion })
                    : ''}
                </p>
              </div>
              <button onClick={() => setEditorSource(null)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              {editorLoading ? (
                <div className="flex items-center justify-center py-16 gap-3">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                  <span className="text-sm text-gray-500">{t('enrichment.jsonLoading', 'Loading JSON…')}</span>
                </div>
              ) : (
                <textarea
                  value={editorText}
                  onChange={e => { setEditorText(e.target.value); setEditorError(null); }}
                  spellCheck={false}
                  className="w-full h-[55vh] font-mono text-xs border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-none"
                />
              )}
              {editorError && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {editorError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button
                onClick={() => setEditorSource(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('enrichment.cancel', 'Cancel')}
              </button>
              {(isAdmin || editorSource.organization_id != null) ? (
                <button
                  onClick={saveEditor}
                  disabled={editorLoading || editorSaving || !editorText.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 transition-colors"
                >
                  {editorSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {t('enrichment.save', 'Save as new version')}
                </button>
              ) : (
                <span className="text-xs text-gray-400">
                  {t('enrichment.sharedReadOnly', 'Shared document — managed by the platform, read-only.')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document-details editor (attached documents) */}
      {tagsSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('enrichment.tagsTitle', 'Document details — "{{name}}"', { name: tagsSource.displayTitle || tagsSource.name })}
                </h3>
                <p className="text-xs text-gray-400">
                  {t('enrichment.tagsSubtitle', 'Answers only attach this document for matching vehicles. Corrections apply immediately.')}
                </p>
              </div>
              <button onClick={() => setTagsSource(null)} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('enrichment.docTitle', 'Document title')}</label>
                <input
                  type="text" value={tagsForm.title}
                  onChange={e => setTagsForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Manual de Usuario MG ZS"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('enrichment.docTypeLabel', 'Document type')}</label>
                <select
                  value={tagsForm.docType}
                  onChange={e => setTagsForm(f => ({ ...f, docType: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                >
                  <option value="owner_manual">{t('enrichment.docType.owner_manual', 'Owner manual')}</option>
                  <option value="maintenance">{t('enrichment.docType.maintenance', 'Maintenance')}</option>
                  <option value="warranty">{t('enrichment.docType.warranty', 'Warranty')}</option>
                  <option value="safety">{t('enrichment.docType.safety', 'Safety')}</option>
                  <option value="spec_sheet">{t('enrichment.docType.spec_sheet', 'Spec sheet')}</option>
                  <option value="other">{t('enrichment.docType.other', 'Other')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('enrichment.tagsMake', 'Make')}</label>
                <input
                  type="text" value={tagsForm.make}
                  onChange={e => setTagsForm(f => ({ ...f, make: e.target.value }))}
                  placeholder="MG"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  {t('enrichment.tagsModels', 'Models')}{' '}
                  <span className="text-gray-400 font-normal">{t('enrichment.tagsModelsHint', '(comma-separated)')}</span>
                </label>
                <input
                  type="text" value={tagsForm.models}
                  onChange={e => setTagsForm(f => ({ ...f, models: e.target.value }))}
                  placeholder="ZS, ZS EV"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('enrichment.tagsYearFrom', 'From year')}</label>
                  <input
                    type="number" value={tagsForm.yearFrom}
                    onChange={e => setTagsForm(f => ({ ...f, yearFrom: e.target.value }))}
                    placeholder="2022"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('enrichment.tagsYearTo', 'To year')}</label>
                  <input
                    type="number" value={tagsForm.yearTo}
                    onChange={e => setTagsForm(f => ({ ...f, yearTo: e.target.value }))}
                    placeholder="2026"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400"
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button
                onClick={() => setTagsSource(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('enrichment.cancel', 'Cancel')}
              </button>
              <button
                onClick={saveTags}
                disabled={tagsSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                {tagsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('enrichment.tagsSave', 'Save details')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Org-assignment modal (admin only) */}
      {isAdmin && (orgsSource || orgsBulk) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {orgsBulk
                    ? t('enrichment.shareBulkTitle', 'Share {{count}} documents', { count: bulkShared().length })
                    : t('enrichment.shareTitle', 'Share "{{name}}"', { name: orgsSource ? titleOf(orgsSource) : '' })}
                </h3>
                <p className="text-xs text-gray-400">
                  {t('enrichment.shareSubtitle', 'Orders in the selected organisations are enriched from this source.')}
                </p>
              </div>
              <button onClick={closeOrgs} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {orgsBulk && (
                <p className="text-xs text-amber-600 mb-3">
                  {t('enrichment.shareBulkHint', 'This REPLACES the assignment list on every selected shared document.')}
                </p>
              )}
              {renderOrgChips(orgsSelection, (id) => toggleOrg(orgsSelection, setOrgsSelection, id))}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button
                onClick={closeOrgs}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('enrichment.cancel', 'Cancel')}
              </button>
              <button
                onClick={saveOrgs}
                disabled={orgsSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-40 transition-colors"
              >
                {orgsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('enrichment.saveAssignments', 'Save assignments')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrichmentSourcesSection;
